/**
 * DRPX Courier — Notification-Hook
 *
 * Supabase Realtime Subscriptions für Push-Notifications. Verwaltet
 * In-App-Notifications, Live-Banner, Browser-Push und E-Mail-
 * Benachrichtigungen.
 *
 * @module hooks/useNotifications
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db } from '../lib/supabase.js';
import { apiSilent } from '../lib/api.js';
import { NOTIF_DEFAULTS } from '../config/constants.js';
import { useTabTitleBlink, sendBrowserNotif } from '../components/pwa/notification-utils.js';
import type { Profile, PushNotifFn, TranslateFn } from '../types.js';
import { loadTranslator } from '../i18n/index.js';
import { personId } from '../lib/zugang.js';

// ════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════

type SetUsers = React.Dispatch<React.SetStateAction<Profile[]>>;

interface NotifItem extends Record<string, unknown> {
  id?: string;
  empfaengerId?: string;
  empfaenger_id?: string;
  tab?: string;
  read?: boolean;
  title?: string;
  sub?: string;
}

interface AdminMsg extends Record<string, unknown> {
  gelesen?: boolean;
}

interface LiveBanner {
  icon: string;
  title: string;
  sub: string;
  btnLabel: string;
  goToTab: string;
  bg: string;
  borderColor: string;
}

interface UseNotificationsOptions {
  user: Profile | null;
  users: Profile[];
  setUsers: SetUsers;
  /** Aktualisiert den Login-Schnappschuss (currentUser). Nötig, weil das
   *  abgeleitete `user`-Objekt in App.tsx currentUser über die users-Liste
   *  legt — ein setUsers allein reicht für adminMessages nicht. */
  setCurrentUser: React.Dispatch<React.SetStateAction<Profile | null>>;
  nav: (tab: string) => void;
  loadData: (rolle: string | undefined, userId?: string) => Promise<void>;
  t: TranslateFn;
  emailNotifs: boolean;
}

interface UseNotificationsReturn {
  notifications: NotifItem[];
  setNotifications: React.Dispatch<React.SetStateAction<NotifItem[]>>;
  notifOpen: boolean;
  setNotifOpen: (v: boolean) => void;
  liveBanner: LiveBanner | null;
  setLiveBanner: (v: LiveBanner | null) => void;
  meineNotifs: NotifItem[];
  adminMsgs: AdminMsg[];
  unreadCount: number;
  markAllRead: () => void;
  markAdminMsgRead: (msgId: string) => void;
  deleteAdminMsg: (msgId: string) => void;
  clickNotif: (notifId: string) => void;
  pushNotif: PushNotifFn;
}

// ════════════════════════════════════════════════════════════════════
// Defaults & Helpers
// ════════════════════════════════════════════════════════════════════


function playNotifSound(): void {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1174, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    /* ignored */
  }
}

// ════════════════════════════════════════════════════════════════════
// Hook
// ════════════════════════════════════════════════════════════════════

export function useNotifications({
  user, users, setUsers, setCurrentUser, nav, loadData, t, emailNotifs,
}: UseNotificationsOptions): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<NotifItem[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [liveBanner, setLiveBanner] = useState<LiveBanner | null>(null);

  // ═══ Subscribe to Supabase realtime notifications ═══
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    function () {
      if (!user) return;
      // Benachrichtigungen gehören der PERSON (empfaenger_id = auth.uid()).
      // Beim Mitarbeiter (DRPX-Pro) ist das nicht die Firma.
      const ich = personId(user);
      const channel = db.subscribeNotifications(ich, function (newNotif) {
        setNotifications(function (prev) {
          return [newNotif as NotifItem].concat(prev);
        });
        playNotifSound();
        const notif = newNotif as NotifItem;
        if (notif.tab === 'auftraege' || notif.tab === 'meine' || notif.tab === 'marktplatz') {
          if (reloadTimer.current) clearTimeout(reloadTimer.current);
          reloadTimer.current = setTimeout(function () {
            loadData(user && user.rolle, user && user.id);
            reloadTimer.current = null;
          }, 2000);
        }
      });
      db.getNotifications(ich)
        .then(function (res) {
          if (res.data) setNotifications(res.data as NotifItem[]);
        })
        .catch(function (e: unknown) {
          console.warn('Notif load error:', e);
        });
      return function () {
        db.unsubscribe(channel);
        if (reloadTimer.current) clearTimeout(reloadTimer.current);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user && user.id],
  );

  // ═══ Derived data ═══
  // Entscheidung 04.06.2026 (verfeinert): Normale Benachrichtigungen zeigen
  // NUR Ungelesenes — lesen = sofort weg. Admin-Nachrichten bleiben dagegen
  // sichtbar (gelesen oder nicht), bis der User sie EXPLIZIT löscht
  // (deleteAdminMsg) — wichtige Ansagen dürfen nicht versehentlich
  // verschwinden.
  const meineNotifs = useMemo(
    function () {
      return notifications.filter(function (n) {
        return (n.empfaengerId || n.empfaenger_id) === personId(user) && !n.read;
      });
    },
    [notifications, user],
  );

  const adminMsgs = useMemo(function () {
    return ((user && user.adminMessages) || []) as AdminMsg[];
  }, [user]);

  const unreadCount = useMemo(function () {
    return meineNotifs.length
      + adminMsgs.filter(function (m) { return !m.gelesen; }).length;
  }, [meineNotifs, adminMsgs]);

  useTabTitleBlink(unreadCount);

  // ═══ Actions ═══
  const markAllRead = useCallback(function () {
    if (user) db.markAllNotifsRead(personId(user));
    setNotifications(function (prev) {
      return prev.map(function (n) {
        return (n.empfaengerId || n.empfaenger_id) === personId(user)
          ? Object.assign({}, n, { read: true })
          : n;
      });
    });
    if (user && user.adminMessages && user.adminMessages.length > 0) {
      const readMsgs = user.adminMessages.map(function (m) {
        return Object.assign({}, m, { gelesen: true });
      });
      setUsers(function (prev) {
        return prev.map(function (u) {
          return u.id === user.id ? Object.assign({}, u, { adminMessages: readMsgs }) : u;
        });
      });
      // WICHTIG: currentUser mit-aktualisieren — das abgeleitete user-Objekt
      // in App.tsx lässt currentUser über die users-Liste gewinnen. Ohne
      // dieses Update blieb die UI auf "ungelesen", obwohl die DB schon
      // gespeichert hatte (Bug 04.06.2026).
      setCurrentUser(function (prev) {
        return prev ? Object.assign({}, prev, { adminMessages: readMsgs }) : prev;
      });
      // Migration 100: RPC statt updateProfile — protect_admin_fields
      // blockiert direkte admin_messages-Updates durch User.
      void apiSilent(db.adminMsgMarkGelesen(), 'adminMsgMarkGelesen:alle');
    }
  }, [user, setUsers, setCurrentUser]);

  /**
   * Markiert EINE Admin-Nachricht als gelesen (Klick auf die Nachricht
   * im Glocken-Dropdown). Gleiches Update-Muster wie markAllRead:
   * users-Liste + currentUser + DB-Persist.
   */
  const markAdminMsgRead = useCallback(function (msgId: string) {
    if (!user || !user.adminMessages || user.adminMessages.length === 0) return;
    const msg = user.adminMessages.find(function (m) { return m.id === msgId; });
    if (!msg || msg.gelesen) return; // unbekannt oder schon gelesen → nichts tun
    const updated = user.adminMessages.map(function (m) {
      return m.id === msgId ? Object.assign({}, m, { gelesen: true }) : m;
    });
    setUsers(function (prev) {
      return prev.map(function (u) {
        return u.id === user.id ? Object.assign({}, u, { adminMessages: updated }) : u;
      });
    });
    setCurrentUser(function (prev) {
      return prev ? Object.assign({}, prev, { adminMessages: updated }) : prev;
    });
    void apiSilent(db.adminMsgMarkGelesen(msgId), 'adminMsgMarkGelesen:einzeln');
  }, [user, setUsers, setCurrentUser]);

  /**
   * Löscht EINE Admin-Nachricht endgültig (expliziter Papierkorb-Klick
   * im Glocken-Dropdown). Admin-Nachrichten verschwinden NUR so —
   * Lesen allein blendet sie nicht aus.
   */
  const deleteAdminMsg = useCallback(function (msgId: string) {
    if (!user || !user.adminMessages || user.adminMessages.length === 0) return;
    const updated = user.adminMessages.filter(function (m) { return m.id !== msgId; });
    if (updated.length === user.adminMessages.length) return; // ID unbekannt
    setUsers(function (prev) {
      return prev.map(function (u) {
        return u.id === user.id ? Object.assign({}, u, { adminMessages: updated }) : u;
      });
    });
    setCurrentUser(function (prev) {
      return prev ? Object.assign({}, prev, { adminMessages: updated }) : prev;
    });
    void apiSilent(db.adminMsgDelete(msgId), 'adminMsgDelete');
  }, [user, setUsers, setCurrentUser]);

  const clickNotif = useCallback(function (notifId: string) {
    db.markNotifRead(notifId);
    setNotifications(function (prev) {
      return prev.map(function (n) {
        return n.id === notifId ? Object.assign({}, n, { read: true }) : n;
      });
    });
    const n = notifications.find(function (x) { return x.id === notifId; });
    if (n && n.tab) nav(n.tab);
    setNotifOpen(false);
  }, [notifications, nav]);

  // ═══ Push Notification (mit Notification-Prefs) ═══
  //
  // 19.09.2026: Texte werden in der Sprache des EMPFÄNGERS gebaut. Vorher
  // bekam `pushNotif` fertige Sätze, gebaut mit dem `t()` des Absenders —
  // Glocke und Mail kamen dadurch z. B. bei einem bulgarischen Fahrer auf
  // Deutsch an, wenn die Firma Deutsch eingestellt hatte. Die Sprache liefert
  // dieselbe Abfrage, die ohnehin die Einstellung des Empfängers holt
  // (Migration 117, `get_notif_delivery`).
  const pushNotif = useCallback<PushNotifFn>(
    async function (empfaengerId, build, goToTab, category) {
      // „An mich selbst" = an die Person am Gerät. Beim Mitarbeiter ist eine
      // Meldung an die Firma (user.id) eine Meldung an den Chef — mit dessen
      // Einstellung und Sprache.
      const isSelf = !!user && empfaengerId === personId(user);
      let mode = 'app';
      // Meldung an sich selbst: eigene Sprache, keine Abfrage nötig.
      let tE: TranslateFn = t;
      if (!isSelf) {
        // Bis 01.09.2026 wurde die Einstellung des Empfängers hier lokal
        // gesucht: `users.find(u => u.id === empfaengerId)`. Das schlug IMMER
        // fehl — die Policy `profiles_select_own_or_admin` gibt einem
        // normalen Nutzer nur das eigene Profil frei. Die Einstellung kommt
        // deshalb über eine SECURITY-DEFINER-Funktion (Migration 117). Die
        // Standardwerte bleiben hier, damit sie nicht zusätzlich in der
        // Datenbank liegen.
        const { pref, lang } = await db.getNotifDelivery(empfaengerId, category);
        if (category) mode = pref || NOTIF_DEFAULTS[category] || 'app';
        if (mode === 'off') return;
        tE = await loadTranslator(lang);
      }
      const { title, sub, emailBody } = build(tE);
      if (mode === 'off') return;
      if (mode === 'app' || mode === 'both') {
        db.pushNotif(empfaengerId, title, sub, goToTab);
        if (user && empfaengerId === personId(user)) {
          const isOrder = goToTab === 'marktplatz';
          // Früher: `title.indexOf('Angebot')` — klappte nur auf Deutsch.
          // Die Kategorie ist sprachunabhängig (19.09.2026).
          const isOffer = goToTab === 'auftraege' && !!category && /angebot/i.test(category);
          setLiveBanner({
            icon: isOrder ? 'package' : isOffer ? 'offer-incoming' : 'email',
            title, sub,
            btnLabel: t('liveNewOrderBtn'),
            goToTab,
            bg: isOrder || isOffer
              ? 'linear-gradient(135deg,#1f2e22,#2d4a32)'
              : 'linear-gradient(135deg,#3f2e1f,#92400e)',
            borderColor: isOrder || isOffer
              ? 'rgba(74,222,128,.3)'
              : 'rgba(245,158,11,.3)',
          });
          if (document.hidden) {
            sendBrowserNotif('DRPX: ' + title, sub, function () {
              if (goToTab) nav(goToTab);
            });
          }
        }
      }
      if (mode === 'email' || mode === 'both') {
        // Ebenfalls seit 01.09.2026: Die Adresse ist im Client nicht
        // bekannt und war es nie — die frühere Suche in `users` lief
        // immer ins Leere, es ging also NIE eine Benachrichtigungs-Mail
        // raus. Die Edge Function löst den Empfänger jetzt anhand der
        // Kennung mit Dienstrechten selbst auf.
        const subject = 'DRPX Courier: ' + title;
        const body = (emailBody || title + '\n\n' + sub) + '\n\n---\n' + tE('hcKurierVermittlung');
        void db.sendEmailNotifToUser(empfaengerId, subject, body);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailNotifs, users, user, t, nav],
  );

  return {
    notifications, setNotifications,
    notifOpen, setNotifOpen,
    liveBanner, setLiveBanner,
    meineNotifs, adminMsgs, unreadCount,
    markAllRead, markAdminMsgRead, deleteAdminMsg, clickNotif, pushNotif,
  };
}
